import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Modal, ModalHeader, ModalBody, ModalFooter } from "../../../elements/Modal";
import { config } from '../../../../../utils/config';
import { MultiSelectForm } from './multiAttribute';
import { getCartItemUserSelections, getLatestPricingInfo } from './utils';
import { startLoading, setCheckOOSStatus } from '../../../../../store/application';
import { currencyFormatter } from "../../../../../utils/currency";
import Button from "../../../elements/button";
import { ReactComponent as CircularLoader } from '../../../../../assets/icons/loader.svg';
import { ReactComponent as IconClose } from '../../../../../assets/icons/multiselect-edit-modal-close-icon.svg';
import './multiattribute.scss';
import { errorTrackingGaEvent } from "../../../../../utils/analytics/google";
import { tierSelectGaEvent } from "../../../../shopping-bag/ga-module";
import i18n from '../../../../../utils/translations/i18n';

import { useUpdateQuantityMutation } from "../../../../../store/newCart";
import { useGetPricingMutation } from "../../../../../store/pricing";
import { rulesValidatorApi } from "../../../../../store/rulesValidator";
import { RootState } from "../../../../../store";
import { TierPricesObject } from "../../../../../models/tiers";
import { GaConstants } from "../../../../../enums/gaConstants";

export const EditModalMultiSelect = ({ cart, lineItemId, multiDataObj, isOpen, toggleModal, attributeDisplayKey }) => {
  const dispatch = useDispatch();
  const { t } = i18n;
  const [updateMultiSelect] = useUpdateQuantityMutation();

  const mainItem: any = cart.lineItems[lineItemId][0].data;
  const [multiDisplayDataObj, setMultiDisplayDataObj] = useState(multiDataObj);
  const [productData, setProductData] = useState(Object({ resMap: {} }));
  const [priceInfo, setPriceInfo] = useState(Object());
  const [loadPriceInfo, setLoadPriceInfo] = useState(false);
  const [minQuantityCondition, setMinQuantityCondition] = useState(false);
  const [zeroQuantity, setZeroQuantity] = useState(false);
  const [noInputError, setNoInputError] = useState(false);
  const [isQuantityChange, setIsQuantityChange] = useState(false);
  const [isUpdateDisable, setIsUpdateDisable] = useState(true);

  const [getPricing] = useGetPricingMutation();

  const { applicationState: { tierPricesData } } = useSelector((state: RootState) => state);

  const tierDataForMainItem: TierPricesObject = tierPricesData[mainItem?.productKey] || [];

  useEffect(() => {
    if (mainItem.productKey) {
      const dataForValidation = {
        userSelections: getCartItemUserSelections(mainItem),
        apparelData: Object.values(multiDataObj),
        apparelSizeAttrKey: mainItem.multiSelectData.editData.originalAttributeKey,
      };
      const productDataObj: any = { resMap: {} };

  
      const promises = dataForValidation.apparelData.map(async (val: any) => {
        const tempSelection = JSON.parse(JSON.stringify(dataForValidation.userSelections));
        tempSelection[dataForValidation.apparelSizeAttrKey] = val.originalAttributeValue;

        let payload: any = {
          productKey: mainItem?.productKey,
          locale: cart?.locale,
          tempSelection:tempSelection,
          ...(mainItem?.isV2Product && {
            isV2Product: mainItem?.isV2Product,
            merchantProductVersion: mainItem?.merchantProductVersion ?? '',
          }),
        }
          
        const response: any = await dispatch(
          rulesValidatorApi.endpoints.validateSelection.initiate(payload)
        );

        const isValid = mainItem?.isV2Product ? response?.data?.isFullyResolved : response?.data?.isValid;
        return { [tempSelection[dataForValidation.apparelSizeAttrKey]]: !isValid };

      });
  
      Promise.all(promises)
        .then((results) => {
          const resMap = Object.assign({}, ...results);
          productDataObj.resMap = resMap;
          setProductData(productDataObj);
        })
        .catch((error) => {
          console.error('Error occurred while resolving promise of validateSelection', error);
        });
    }
    getPriceInfo(getTotalQuantity(Object.values(multiDataObj)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart]);

  const getTotalQuantity = (inputArray) => inputArray.reduce((sum, { qty }) => sum + qty, 0);

  const getPriceInfo = async (qty) => {
    const productPricing =  await getPricing({productKey: mainItem.productKey, qty: qty});
    setLoadPriceInfo(false);
    setPriceInfo({ ...productPricing["data"] });
  }

  const closeModal = () => {
    setMultiDisplayDataObj(multiDataObj);
    setLoadPriceInfo(true);
    getPriceInfo(getTotalQuantity(Object.values(multiDataObj)));
    setMinQuantityCondition(false);
    setNoInputError(false);
    setZeroQuantity(false);
    setIsQuantityChange(false);
    toggleModal();
    dispatch(setCheckOOSStatus(false));
  }

  const handleBlur = () => {
    if (isQuantityChange === true) {
      setIsQuantityChange(false);
    }
  };

  const handleChange = (index, value) => {
    const prevQty = getTotalQuantity(Object.values(multiDisplayDataObj));
    const newArr = { ...multiDisplayDataObj }
    const obj = newArr[index];
    const newObj = { ...obj, qty: value };
    newArr[index] = newObj;
    setMultiDisplayDataObj(newArr);
    const qty = getTotalQuantity(Object.values(newArr));
    if (qty < tierDataForMainItem?.tierObject[0].quantity) {
      if (qty === 0) {
        setIsQuantityChange(false);
        setZeroQuantity(true);
        setMinQuantityCondition(false);
      } else {
        setIsQuantityChange(false);
        setZeroQuantity(false);
        setMinQuantityCondition(true);
        setNoInputError(false);
        errorTrackingGaEvent("product-details-page", "Minimum order quantity - " + tierDataForMainItem?.tierObject[0].quantity);
      }
    }
    if (qty !== prevQty && qty >= tierDataForMainItem?.tierObject[0].quantity) {
      setZeroQuantity(false);
      setMinQuantityCondition(false);
      setNoInputError(false);
      setLoadPriceInfo(true);
      getPriceInfo(qty);
      setIsQuantityChange(true);
    }
  }

  const saveAndUpdateCart = async () => {
    const qty = getTotalQuantity(Object.values(multiDisplayDataObj));
    const output = tierDataForMainItem?.tierObject.reduce((prev, curr) => {
      if (qty - Number(curr.quantity) < 0) return prev;
      return curr;
    });
    const tierObjectMultiSelect = {
      qty: qty,
      price: output.unitPrice,
    };
    tierSelectGaEvent(tierObjectMultiSelect, 0, true, mainItem, GaConstants.BOX, cart?.fractionDigits);
    dispatch(setCheckOOSStatus(false));

    if (!minQuantityCondition) {
      if (getTotalQuantity(Object.values(multiDisplayDataObj)) === 0) {
        setNoInputError(true);
        errorTrackingGaEvent("product-details-page", "Please enter a quantity");
        return;
      };

      let multiValueStr:any = []
      Object.keys(multiDisplayDataObj).forEach((key, index)=>{
       !productData.resMap[key] &&
        multiValueStr.push({
          "value": mainItem.isV2Product ? multiDisplayDataObj[key].originalAttributeValue : key,
          "quantity":multiDisplayDataObj[key].qty
        });
      })

      dispatch(startLoading());
      updateMultiSelect({
        cartId: cart.id,
        lineItems: {
          "data": {
            "type": "mainItem",
            "attributes": {
              "lineItemId": mainItem.id,
              "multiAttributeLineItem": true,
              "multiAttributeName": attributeDisplayKey,
              "multiAttributeSelections": multiValueStr,
            }
          }
        },
        isV2Product:mainItem?.isV2Product
      })
      toggleModal();
    }
  }

  const editHeader = <>
    <ModalHeader>
      <>
        <h3 className="edit-modal-mega">{t('product_details.edit_number_of_units')}</h3>
      </>
    </ModalHeader>
  </>;

  const editBody = <ModalBody>
    <>
      <MultiSelectForm
        isShoppingFeed={false}
        multiDisplayDataObj={multiDisplayDataObj}
        tierPricesData={tierDataForMainItem}
        handleChange={handleChange}
        handleBlur={handleBlur}
        resMap={productData.resMap}
        minQuantityCondition={minQuantityCondition}
        noinputError={noInputError}
        setIsUpdateDisable={setIsUpdateDisable}
        productKey={mainItem?.productKey}
        mainItem={mainItem}
      />
      {/* total quantity */}
      <div className="summary-total edit-quantity-summary-total summary-total-quantity">
        <span>{t('product_details.total_quantity')}</span>
        <span className="summary-total__price">
          <span id="total-quantity-multi-select" className="summary-total-amount">
            {(minQuantityCondition) ? 0 : (loadPriceInfo ? <CircularLoader className="summary-loader" /> : getTotalQuantity(Object.values(multiDisplayDataObj)))}
          </span>
          <div className="summary-total-loader-container"></div>
        </span>
      </div>

      {/* unit price */}
      <div className="summary-total edit-quantity-summary-total">
        <span>{t('product_details.unit_price')}</span>
        <span className="summary-total__price">
          <span id="unit-price-multi-select" className="summary-total-amount">
            <div>
              {(minQuantityCondition || zeroQuantity) ? currencyFormatter(0, cart.fractionDigits) : loadPriceInfo ? <CircularLoader className="summary-loader" /> : getLatestPricingInfo(priceInfo, 'unit-price', cart.fractionDigits)}
            </div>
          </span>
        </span>
      </div>

      {/* price excluding vat */}
      {config('isNASite') !== true && <>
        <div className="summary-total edit-quantity-summary-total">
          <span>{t('product_details.price_excl_vat')}</span>
          <span className="summary-total__price">
            <span id="excluding-vat-price" className="summary-total-amount">
              <div>
                {(minQuantityCondition || zeroQuantity) ? currencyFormatter(0, cart.fractionDigits) : loadPriceInfo ? <CircularLoader className="summary-loader" /> : getLatestPricingInfo(priceInfo, 'price-excl-vat', cart.fractionDigits)}
              </div>
            </span>
          </span>
        </div>

        <div className="summary-total edit-quantity-total-price">
          <span className="summary-price_incl_vat">{t('product_details.price_incl_vat')}</span>
          <span className="summary-total__price">
            <span id="including-vat-price" className="summary-total-amount-bold">
              {(minQuantityCondition || zeroQuantity) ? currencyFormatter(0, cart.fractionDigits) : loadPriceInfo ? <CircularLoader className="summary-loader" /> : getLatestPricingInfo(priceInfo, 'price-incl-vat', cart.fractionDigits)}
            </span>
          </span>
        </div>
      </>}
      {config('isNASite') === true && <>
        <div className="summary-total edit-quantity-total-price">
          <span>{t('product_details.price')}</span>
          <span className="summary-total__price">
            <span id="excluding-vat-price" className="summary-total-amount-bold">
              <div>
                {(minQuantityCondition || zeroQuantity) ? currencyFormatter(0, cart.fractionDigits) : loadPriceInfo ? <CircularLoader className="summary-loader-na" /> : getLatestPricingInfo(priceInfo, 'price-excl-vat', cart.fractionDigits)}
              </div>
            </span>
          </span>
        </div>
      </>}

    </>
  </ModalBody>

  const editFooter = <ModalFooter>
    <>
      <Button isDisabled={isUpdateDisable} className="save-add-cart btn--full-width btn--pill save-add-cart-multi" onClick={saveAndUpdateCart} children={t('shopping_cart_details.saveAndUpdate')} style={{ textTransform:'none'}} />
      <div className="error-edit-qty"> {t('shopping_cart_details.changeQtyError')}</div>
    </>
  </ModalFooter>

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={closeModal}
        customClass={'edit-quantity-mfp-inner'}
        customOuterClass={'edit-quantity-mfp'}
      >
        <>
          <div className='edit-modal-close' >
            <IconClose onClick={closeModal}/>
          </div>
          {editHeader}
          {editBody}
          {editFooter}
        </>
      </Modal>
    </>
  );
};
